import { Link } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle2,
  HardDrive,
  Laptop,
  MessageCircle,
  Monitor,
  Sparkles,
  Users,
  Wrench,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BLOG_POSTS, getHomeBlogPreview } from '../data/blogPosts';
import { openSalesIQChat } from '../utils/chat';
import { COMPANY_ADDRESS, COMPANY_NAME, SITE_NAME } from '../constants/site';

const servicePillars = [
  {
    title: 'First-Time Setup',
    text: 'Walk through download, activation, and your first tree—without skipping the backup step that saves hours later.',
    Icon: Laptop,
    tag: 'Start here',
  },
  {
    title: 'PC & Mac Moves',
    text: 'Plan folder paths, copy media in one pass, and confirm people, sources, and thumbnails on the new machine.',
    Icon: HardDrive,
    tag: 'Migration',
  },
  {
    title: 'Sync & Cloud Fixes',
    text: 'Resolve TreeVault stalls, duplicate profiles, and devices that disagree after an update or Wi‑Fi change.',
    Icon: Wrench,
    tag: 'Troubleshoot',
  },
  {
    title: 'Research Workflows',
    text: 'FamilySearch hints, citation habits, and chart exports that still read clearly when you print for relatives.',
    Icon: BookOpen,
    tag: 'Learn',
  },
];

const migrationSteps = [
  { step: '01', title: 'Snapshot', detail: 'Export a dated backup and open it once to confirm it is not empty.' },
  { step: '02', title: 'Install', detail: 'Match the major version on the new computer before you import anything.' },
  { step: '03', title: 'Restore', detail: 'Bring the tree file and the full media folder over together when possible.' },
  { step: '04', title: 'Verify', detail: 'Spot-check ten people, five sources, and a handful of photos or PDFs.' },
  { step: '05', title: 'Archive', detail: 'Store a second copy off-site only after the new setup passes your checklist.' },
];

const audiences = [
  {
    title: 'New Researchers',
    text: 'You want a calm order of operations—what to click, what to avoid, and how to keep one master copy.',
    Icon: Sparkles,
  },
  {
    title: 'Society Volunteers',
    text: 'You maintain shared lines and need GEDCOM imports that do not explode into duplicate branches.',
    Icon: Users,
  },
  {
    title: 'Power Users',
    text: 'Large media libraries, NAS paths, and multi-device sync—documented steps beat guesswork every time.',
    Icon: Monitor,
  },
];

const workspaceTools = [
  { name: 'Pedigree Canvas', note: 'Scan generations at a glance' },
  { name: 'Household Lens', note: 'See parents, spouses, and children together' },
  { name: 'Source Locker', note: 'Keep citations beside each fact' },
  { name: 'Media Vault', note: 'Tag scans, photos, and PDFs once' },
  { name: 'Chart Studio', note: 'Fan, bow-tie, and wall layouts' },
  { name: 'Research Journal', note: 'Log what you tried and what worked' },
];

const painPoints = [
  { label: 'Activation loops', hint: 'Product key or sign-in will not stick after reinstall.' },
  { label: 'Broken thumbnails', hint: 'Photos show a red X after copying folders.' },
  { label: 'GEDCOM surprises', hint: 'Notes vanish or duplicates appear on import.' },
  { label: 'Slow sync', hint: 'One laptop updates; the other waits forever.' },
  { label: 'Upgrade crashes', hint: 'Installer quits mid-way on Windows or Mac.' },
  { label: 'Missing spouses', hint: 'Relationships look fine on one device only.' },
];

const stories = [
  {
    quote: 'They walked me through a Mac transfer without me losing twenty years of scans. The checklist alone was worth the chat session.',
    name: 'Elena R.',
    role: 'Hobbyist · Portland, OR',
  },
  {
    quote: 'Our society tree had tripled in size. The GEDCOM cleanup steps finally made merges predictable.',
    name: 'David M.',
    role: 'Genealogy club · Columbus, OH',
  },
  {
    quote: 'I thought cloud sync was broken. Turns out my media path moved—fixed in one session with chat support.',
    name: 'Priya K.',
    role: 'Family historian · Tampa, FL',
  },
];

const HERO_CHECKLIST = [
  'Family Tree Maker download copy',
  'Family Tree guides',
  'Family Tree Maker upgrade',
  'Family tree maker setup guides',
  'Family tree maker backup guides',
  'Family tree maker upgrade guides',
  'Family tree maker sync error fix',
  'Family tree maker no internet fix guide',
  'Family tree maker installation guides',
];

function HomePageRedesigned() {
  const pub = process.env.PUBLIC_URL || '';

  return (
    <div className="ftm-page">
      <div className="s4h-hero-shell">
        <Header variant="green" />

        <section id="home" className="s4h-hero" aria-labelledby="s4h-hero-title">
          <div className="s4h-hero__grid">
            <div className="s4h-hero__panel">
              <div className="s4h-hero__curve" aria-hidden />
              <div className="s4h-hero__panel-inner">
                <p className="s4h-hero__intro">
                  Family Tree Maker is not just software—it is a treasured platform to store your family history. That
                  is why {SITE_NAME} provides dedicated support for your tree, backups, upgrades, and sync.
                </p>
                <h1 id="s4h-hero-title">Family Tree Maker</h1>
                <ul className="s4h-hero__checklist">
                  {HERO_CHECKLIST.map((item, index) => (
                    <li key={`${item}-${index}`}>
                      <CheckCircle2 size={20} aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="s4h-hero__media">
              <img src={`${pub}/hero_bg.jpg`} alt="Family archive photograph" loading="eager" />
            </div>
          </div>
        </section>
      </div>

      <main>
        {/* About */}
        <section id="about" className="t5-section t5-section--alt">
          <div className="t5-wrap">
            <div className="t5-split-head">
              <div>
                <span className="t5-kicker">Who we are</span>
                <h2>Guides written for real desktops—not marketing slides</h2>
              </div>
              <p>
                {SITE_NAME} publishes practical playbooks: which folders to copy, how to test a backup,
                and what to screenshot when something fails. Every article assumes you care about the tree you already
                built.
              </p>
            </div>
            <div className="t5-about-panel">
              <div className="t5-about-panel__text">
                <h3>What makes our help different</h3>
                <ul className="t5-checklist">
                  <li>Checklists you can print and tick off during a migration</li>
                  <li>No upsell to unrelated software—genealogy context only</li>
                  <li>Live chat that asks for version + OS up front</li>
                  <li>Reminders to keep a second backup before risky merges</li>
                </ul>
              </div>
              <aside className="t5-about-aside">
                <p className="t5-about-aside__label">Today&apos;s focus</p>
                <p className="t5-about-aside__title">Spring upgrade season</p>
                <p className="t5-about-aside__body">
                  Top topics this month: Windows 11 moves, Mac media paths, and TreeVault sign-in after router changes.
                </p>
              </aside>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="t5-section">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">How we help</span>
              <h2>Four ways we support your research</h2>
              <p>Pick the lane that matches your screen right now—we will meet you there.</p>
            </div>
            <div className="t5-bento">
              {servicePillars.map(({ title, text, Icon, tag }) => (
                <article key={title} className="t5-bento-card">
                  <span className="t5-bento-tag">{tag}</span>
                  <div className="t5-bento-icon">
                    <Icon size={26} strokeWidth={1.6} aria-hidden />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Support CTA */}
        <section className="t5-cta-band" aria-labelledby="t5-cta-title">
          <div className="t5-wrap t5-cta-band__inner">
            <div className="t5-cta-band__copy">
              <span className="t5-kicker t5-kicker--light">Live assistance</span>
              <h2 id="t5-cta-title">Stuck mid-upgrade? Talk before you force-quit the installer</h2>
              <p>
                Describe the last successful step, your operating system, and whether the media folder still lives on
                an external drive. We will suggest the safest next click—not a factory reset.
              </p>
            </div>
            <div className="t5-cta-band__actions">
              <button type="button" className="t5-btn t5-btn--white" onClick={openSalesIQChat}>
                <MessageCircle size={20} aria-hidden />
                Open live chat
              </button>
            </div>
          </div>
        </section>

        {/* Migration timeline */}
        <section id="guides" className="t5-section t5-section--alt">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">New computer</span>
              <h2>Five-step transfer path</h2>
              <p>Follow the sequence in order—each step earns the next one.</p>
            </div>
            <ol className="t5-timeline">
              {migrationSteps.map(({ step, title, detail }) => (
                <li key={step} className="t5-timeline-item">
                  <span className="t5-timeline-step">{step}</span>
                  <div className="t5-timeline-body">
                    <h3>{title}</h3>
                    <p>{detail}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Audiences */}
        <section id="audience" className="t5-section">
          <div className="t5-wrap">
            <div className="t5-split-head">
              <div>
                <span className="t5-kicker">Who we serve</span>
                <h2>Same software, different starting points</h2>
              </div>
              <p>We tune explanations to how much history you already carry—not how technical you feel.</p>
            </div>
            <div className="t5-audience-row">
              {audiences.map(({ title, text, Icon }) => (
                <article key={title} className="t5-audience-card">
                  <div className="t5-audience-icon">
                    <Icon size={24} aria-hidden />
                  </div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Workspace tools */}
        <section id="features" className="t5-section t5-section--alt">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">Inside the workspace</span>
              <h2>Tools researchers reach for daily</h2>
              <p>Names reflect how we teach the interface—your menus may use slightly different labels.</p>
            </div>
            <div className="t5-tool-grid">
              {workspaceTools.map(({ name, note }) => (
                <div key={name} className="t5-tool-chip">
                  <CheckCircle2 size={18} className="t5-tool-chip__icon" aria-hidden />
                  <div>
                    <strong>{name}</strong>
                    <span>{note}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section id="requirements" className="t5-section t5-req">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">Compatibility</span>
              <h2>Recommended environment</h2>
              <p>Headroom beats minimums when media libraries grow past a few gigabytes.</p>
            </div>
            <div className="t5-req-grid">
              <article className="t5-req-card">
                <h3>Windows</h3>
                <ul>
                  <li>Windows 10 or 11, 64-bit</li>
                  <li>Intel / AMD 2 GHz or faster</li>
                  <li>16 GB RAM preferred (8 GB minimum)</li>
                  <li>SSD with 5+ GB free for trees + media cache</li>
                  <li>Broadband for activation &amp; TreeVault</li>
                </ul>
              </article>
              <article className="t5-req-card">
                <h3>macOS</h3>
                <ul>
                  <li>macOS 12 Monterey or newer</li>
                  <li>Apple Silicon or Intel Core i5+</li>
                  <li>16 GB RAM preferred (8 GB minimum)</li>
                  <li>Retina display 1280×800 or higher</li>
                  <li>Broadband for activation &amp; TreeVault</li>
                </ul>
              </article>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="stories" className="t5-section t5-section--alt">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">Voices</span>
              <h2>Researchers who chatted before deleting old files</h2>
            </div>
            <div className="t5-story-grid">
              {stories.map(({ quote, name, role }) => (
                <blockquote key={name} className="t5-story-card">
                  <p>&ldquo;{quote}&rdquo;</p>
                  <footer>
                    <strong>{name}</strong>
                    <span>{role}</span>
                  </footer>
                </blockquote>
              ))}
            </div>
          </div>
        </section>

        {/* Issues */}
        <section id="issues" className="t5-section">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">Symptom finder</span>
              <h2>Problems we untangle every week</h2>
              <p>Tap chat with the label that sounds closest—we will narrow from there.</p>
            </div>
            <div className="t5-issue-grid">
              {painPoints.map(({ label, hint }) => (
                <button key={label} type="button" className="t5-issue-pill" onClick={openSalesIQChat}>
                  <span className="t5-issue-pill__label">{label}</span>
                  <span className="t5-issue-pill__hint">{hint}</span>
                </button>
              ))}
            </div>
            <div className="t5-center-cta">
              <button type="button" className="t5-btn t5-btn--solid" onClick={openSalesIQChat}>
                <MessageCircle size={20} aria-hidden />
                Still stuck? Open chat
              </button>
            </div>
          </div>
        </section>

        {/* Blog / Resources — reference-style stacked articles */}
        <section id="blog" className="t5-section t5-section--alt t5-blog-ref">
          <div className="t5-wrap t5-blog-ref__wrap">
            <header className="t5-blog-ref__intro">
              <h2>Smart solutions for your Family Tree Maker workspace</h2>
              <p>
                Step-by-step guides on transfer, upgrade, download, sync, backup, and media—written the way our support
                team explains fixes on live chat. Each preview below includes several sections from the full article; use
                Read More for the complete guide, checklists, and troubleshooting steps.
              </p>
            </header>

            <div className="t5-blog-ref__chat-top">
              <button type="button" className="t5-blog-ref__chat-btn" onClick={openSalesIQChat}>
                <MessageCircle size={18} aria-hidden />
                Chat Now
              </button>
            </div>

            <div className="t5-blog-ref__list">
              {BLOG_POSTS.map((post, index) => (
                <div key={post.slug}>
                  <article className="t5-blog-ref__item">
                    <div className="t5-blog-ref__item-head">
                      <span className="t5-blog-ref__topic">{post.topic}</span>
                      <span className="t5-blog-ref__meta">
                        {post.date} · {post.readTime}
                      </span>
                    </div>
                    <h3>{post.title}</h3>
                    <p className="t5-blog-ref__excerpt">{post.excerpt}</p>
                    <div className="t5-blog-ref__preview">
                      {getHomeBlogPreview(post).map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                    <Link to={`/blog/${post.slug}`} className="t5-blog-ref__read">
                      Read More
                    </Link>
                  </article>
                  {(index + 1) % 3 === 0 && index < BLOG_POSTS.length - 1 && (
                    <div className="t5-blog-ref__chat-mid">
                      <button type="button" className="t5-blog-ref__chat-btn" onClick={openSalesIQChat}>
                        <MessageCircle size={18} aria-hidden />
                        Chat Now
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <footer className="t5-blog-ref__footer">
              <Link to="/blog" className="t5-blog-ref__more">
                <BookOpen size={18} aria-hidden />
                View more articles
              </Link>
            </footer>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="t5-section t5-contact">
          <div className="t5-wrap">
            <div className="t5-center-head">
              <span className="t5-kicker">Contact</span>
              <h2>Connect with a technician in live chat</h2>
              <p>
                Open chat and share your software version, operating system, and what you already tried—we will guide
                you from there.
              </p>
            </div>
            <div className="t5-contact-chat-panel">
              <aside className="t5-contact-card">
                <h3>{COMPANY_NAME}</h3>
                <p>{COMPANY_ADDRESS}</p>
                <p className="t5-contact-note">
                  Support is available through live chat. Have your backup status and error message ready for a faster
                  fix.
                </p>
              </aside>
              <div className="t5-contact-chat-cta">
                <MessageCircle size={48} strokeWidth={1.5} aria-hidden />
                <h3>Ready when you are</h3>
                <p>Click below to start a live chat session with our Family Tree Maker support team.</p>
                <button type="button" className="t5-btn t5-btn--solid" onClick={openSalesIQChat}>
                  <MessageCircle size={20} aria-hidden />
                  Open live chat now
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default HomePageRedesigned;
